import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { cashLedgerAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Money } from '@phosphor-icons/react';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function CashLedgerPage() {
  const [summary, setSummary] = useState({ locations: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await cashLedgerAPI.summary(); setSummary(data); }
    catch (e) { toast.error('Failed to load cash ledger'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openLocation = async (loc) => {
    setSelected(loc); setEntriesLoading(true);
    try { const { data } = await cashLedgerAPI.list({ clinic_id: loc.clinic_id, limit: 200 }); setEntries(data.entries || []); }
    catch (e) { toast.error('Failed to load entries'); } finally { setEntriesLoading(false); }
  };

  return (
    <div data-testid="cash-ledger-page">
      <PageHeader title="Cash Ledger" subtitle="Real-time cash in hand across all clinic locations" />
      <div className="page-content">
        {!selected ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4"><p className="text-xs text-[#64748B] mb-1">Total Cash In</p><p className="text-lg font-semibold text-emerald-600">{fmt(summary.totals?.cash_in)}</p></div>
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4"><p className="text-xs text-[#64748B] mb-1">Total Cash Out</p><p className="text-lg font-semibold text-red-600">{fmt(summary.totals?.cash_out)}</p></div>
              <div className="bg-[#0F4C81] text-white rounded-lg p-4"><p className="text-xs text-white/70 mb-1">Net Cash In Hand</p><p className="text-lg font-semibold">{fmt(summary.totals?.balance)}</p></div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="cash-summary-table">
                <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-xs"><th className="text-left p-3">Location</th><th className="text-right p-3">Cash In</th><th className="text-right p-3">Cash Out</th><th className="text-right p-3">Cash In Hand</th><th className="text-center p-3">Entries</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
                  ) : summary.locations.length > 0 ? summary.locations.map(loc => (
                    <tr key={loc.clinic_id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer" onClick={() => openLocation(loc)} data-testid={`cash-loc-${loc.clinic_id}`}>
                      <td className="p-3 font-medium text-[#0F4C81]">{loc.clinic_name || loc.clinic_id}</td>
                      <td className="p-3 text-right text-emerald-600">{fmt(loc.cash_in)}</td>
                      <td className="p-3 text-right text-red-600">{fmt(loc.cash_out)}</td>
                      <td className="p-3 text-right font-semibold">{fmt(loc.balance)}</td>
                      <td className="p-3 text-center text-[#64748B]">{loc.entries}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="text-center py-10 text-[#64748B]"><Money size={28} className="mx-auto mb-2 opacity-40" />No cash entries yet. Cash payments and clinic expenses will appear here.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="mb-3" data-testid="cash-back-btn"><ArrowLeft size={16} className="mr-1" /> All Locations</Button>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans']">{selected.clinic_name}</h2>
              <div className="text-right"><p className="text-xs text-[#64748B]">Cash In Hand</p><p className="text-xl font-bold text-[#0F4C81]">{fmt(selected.balance)}</p></div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-xs"><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Source</th><th className="text-left p-3">Remarks / Ref</th><th className="text-right p-3">Amount</th></tr></thead>
                <tbody>
                  {entriesLoading ? (
                    <tr><td colSpan={5} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
                  ) : entries.length > 0 ? entries.map(e => (
                    <tr key={e.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                      <td className="p-3 text-[#64748B]">{e.entry_date}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 text-xs rounded ${e.type === 'in' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{e.type === 'in' ? 'Cash In' : 'Cash Out'}</span></td>
                      <td className="p-3 text-[#64748B] capitalize">{(e.source || '').replace(/_/g, ' ')}</td>
                      <td className="p-3">{e.remarks || '-'}{e.ref_number ? <span className="text-xs text-[#64748B] ml-1 font-mono">{e.ref_number}</span> : null}</td>
                      <td className={`p-3 text-right font-medium ${e.type === 'in' ? 'text-emerald-700' : 'text-red-700'}`}>{e.type === 'in' ? '+' : '−'}{fmt(e.amount)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="text-center py-8 text-[#64748B]">No entries for this location.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
